<template>
  <div class="py-4 container-fluid">
    <template v-if="!isLoading">
      <!-- Filtro por semana (primero) -->
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 class="mb-0 text-dark">Asistencias por semana</h5>
        <router-link to="/configuraciones" class="btn btn-sm btn-outline-secondary mb-0">
          <i class="fas fa-cog me-1" aria-hidden="true"></i> Configurar semanas
        </router-link>
      </div>

      <div class="d-flex flex-wrap gap-2 mb-2">
        <button
          type="button"
          class="btn btn-sm mb-0"
          :class="selected === 'today' ? 'bg-gradient-primary text-white' : 'btn-outline-primary'"
          @click="selectFilter('today')"
        >
          Hoy
        </button>
        <button
          v-for="week in weeks"
          :key="week.id"
          type="button"
          class="btn btn-sm mb-0 text-start"
          :class="selected === week.id ? 'bg-gradient-primary text-white' : 'btn-outline-primary'"
          @click="selectFilter(week.id)"
        >
          Semana {{ week.number }}
          <span class="d-block text-xxs opacity-8">{{ formatRange(week) }}</span>
        </button>
        <button
          type="button"
          class="btn btn-sm mb-0"
          :class="selected === 'all' ? 'bg-gradient-primary text-white' : 'btn-outline-primary'"
          @click="selectFilter('all')"
        >
          Todo el programa
        </button>
      </div>

      <p v-if="!weeks.length" class="text-xs text-secondary mb-2">
        No hay semanas configuradas.
        <router-link to="/configuraciones">Configúralas</router-link>
        para filtrar por semana.
      </p>

      <!-- Totales vinculados al filtro seleccionado -->
      <p class="text-sm text-secondary mb-2">
        Mostrando: <span class="font-weight-bold">{{ filterLabel }}</span>
      </p>
      <div class="row">
        <div class="col-12 col-md-4">
          <mini-statistics-card
            title="Total"
            :value="currentEntrances.total ?? 0"
            :icon="{ component: 'fa fa-users', background: 'bg-gradient-primary' }"
            direction-reverse
          />
        </div>
        <div class="col-12 col-md-4">
          <mini-statistics-card
            title="Infantil"
            :value="currentEntrances.infants ?? 0"
            :icon="{ component: 'fa fa-child', background: 'bg-gradient-success' }"
            direction-reverse
          />
        </div>
        <div class="col-12 col-md-4">
          <mini-statistics-card
            title="Juvenil"
            :value="currentEntrances.youths ?? 0"
            :icon="{ component: 'fa fa-user-graduate', background: 'bg-gradient-info' }"
            direction-reverse
          />
        </div>
      </div>

      <!-- Equipos juveniles por continente -->
      <h5 class="mt-2 mb-3 text-dark">Equipos juveniles por continente</h5>
      <div class="row">
        <div v-for="(paises, continent) in youthByContinent" :key="continent" class="col-12 col-lg-4">
          <div class="card mb-4 continent-card" :style="continentCardStyle(continent)">
            <div class="card-header pb-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0" :style="{ color: continentColor(continent) }">{{ continent }}</h6>
              <div class="d-flex align-items-center gap-2">
                <span class="badge continent-total-badge" :style="continentBadgeStyle(continent)">
                  {{ continentTotal(paises) }} participantes
                </span>
                <div class="dropdown download-actions">
                  <a
                    :id="`download-cont-${continent}`"
                    class="download-menu-trigger cursor-pointer"
                    href="javascript:;"
                    :aria-expanded="openDownloadMenu === `continent-${continent}`"
                    aria-label="Descargar listado del continente"
                    @click.stop.prevent="toggleDownloadMenu(`continent-${continent}`)"
                  >
                    <i class="fas fa-download text-secondary" aria-hidden="true"></i>
                  </a>
                  <ul
                    class="dropdown-menu dropdown-menu-end px-2 py-3"
                    :class="{ show: openDownloadMenu === `continent-${continent}` }"
                    :aria-labelledby="`download-cont-${continent}`"
                  >
                    <li>
                      <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadContinent(continent, 'excel')">
                        <i class="fas fa-file-excel text-success me-2" aria-hidden="true"></i>
                        Descargar Excel
                      </a>
                    </li>
                    <li>
                      <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadContinent(continent, 'pdf')">
                        <i class="fas fa-file-pdf text-danger me-2" aria-hidden="true"></i>
                        Descargar PDF
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="card-body pt-2">
              <ul class="list-group">
                <li
                  v-for="team in paises"
                  :key="team.id"
                  class="list-group-item d-flex justify-content-between align-items-center px-0 py-2 border-0 border-bottom"
                >
                  <span class="text-sm font-weight-bold text-dark">{{ team.name }}</span>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-light text-dark">{{ team.members_count }}</span>
                    <div class="dropdown download-actions">
                      <a
                        :id="`download-youth-${team.id}`"
                        class="download-menu-trigger cursor-pointer"
                        href="javascript:;"
                        :aria-expanded="openDownloadMenu === `youth-${team.id}`"
                        aria-label="Opciones de descarga"
                        @click.stop.prevent="toggleDownloadMenu(`youth-${team.id}`)"
                      >
                        <i class="fas fa-download text-secondary" aria-hidden="true"></i>
                      </a>
                      <ul
                        class="dropdown-menu dropdown-menu-end px-2 py-3"
                        :class="{ show: openDownloadMenu === `youth-${team.id}` }"
                        :aria-labelledby="`download-youth-${team.id}`"
                      >
                        <li>
                          <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadTeam(team, 'youth', 'excel')">
                            <i class="fas fa-file-excel text-success me-2" aria-hidden="true"></i>
                            Descargar Excel
                          </a>
                        </li>
                        <li>
                          <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadTeam(team, 'youth', 'pdf')">
                            <i class="fas fa-file-pdf text-danger me-2" aria-hidden="true"></i>
                            Descargar PDF
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Equipos infantiles -->
      <h5 class="mt-2 mb-3 text-dark">Equipos infantiles</h5>
      <div class="row">
        <div class="col-12 col-lg-6">
          <div class="card mb-4">
            <div class="card-header pb-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0 text-success">Infantil</h6>
              <span class="badge bg-gradient-success">{{ infantTotal }} participantes</span>
            </div>
            <div class="card-body pt-2">
              <ul class="list-group">
                <li
                  v-for="team in infantTeams"
                  :key="team.id"
                  class="list-group-item d-flex justify-content-between align-items-center px-0 py-2 border-0 border-bottom"
                >
                  <span class="text-sm font-weight-bold text-dark">{{ team.name }}</span>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-light text-dark">{{ team.members_count }}</span>
                    <div class="dropdown download-actions">
                      <a
                        :id="`download-infant-${team.id}`"
                        class="download-menu-trigger cursor-pointer"
                        href="javascript:;"
                        :aria-expanded="openDownloadMenu === `infant-${team.id}`"
                        aria-label="Opciones de descarga"
                        @click.stop.prevent="toggleDownloadMenu(`infant-${team.id}`)"
                      >
                        <i class="fas fa-download text-secondary" aria-hidden="true"></i>
                      </a>
                      <ul
                        class="dropdown-menu dropdown-menu-end px-2 py-3"
                        :class="{ show: openDownloadMenu === `infant-${team.id}` }"
                        :aria-labelledby="`download-infant-${team.id}`"
                      >
                        <li>
                          <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadTeam(team, 'infant', 'excel')">
                            <i class="fas fa-file-excel text-success me-2" aria-hidden="true"></i>
                            Descargar Excel
                          </a>
                        </li>
                        <li>
                          <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadTeam(team, 'infant', 'pdf')">
                            <i class="fas fa-file-pdf text-danger me-2" aria-hidden="true"></i>
                            Descargar PDF
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Totales por reto -->
      <h5 class="mt-2 mb-3 text-dark">Retos</h5>
      <div class="row">
        <div v-for="block in retoTotals" :key="block.reto" class="col-12 col-lg-4">
          <div class="card mb-4">
            <div class="card-header pb-2 d-flex justify-content-between align-items-center">
              <h6 class="mb-0 text-info">Reto {{ block.reto }}</h6>
              <span class="badge bg-gradient-info">{{ retoTotal(block) }} asignados</span>
            </div>
            <div class="card-body pt-2">
              <ul v-if="assigned(block).length" class="list-group">
                <li
                  v-for="w in assigned(block)"
                  :key="w.id"
                  class="list-group-item d-flex justify-content-between align-items-center px-0 py-2 border-0 border-bottom"
                >
                  <span class="text-sm font-weight-bold text-dark">{{ w.name }}</span>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-light text-dark">{{ w.members_count }}</span>
                    <div class="dropdown download-actions">
                      <a
                        :id="`download-workshop-${block.reto}-${w.id}`"
                        class="download-menu-trigger cursor-pointer"
                        href="javascript:;"
                        :aria-expanded="openDownloadMenu === `workshop-${block.reto}-${w.id}`"
                        aria-label="Opciones de descarga"
                        @click.stop.prevent="toggleDownloadMenu(`workshop-${block.reto}-${w.id}`)"
                      >
                        <i class="fas fa-download text-secondary" aria-hidden="true"></i>
                      </a>
                      <ul
                        class="dropdown-menu dropdown-menu-end px-2 py-3"
                        :class="{ show: openDownloadMenu === `workshop-${block.reto}-${w.id}` }"
                        :aria-labelledby="`download-workshop-${block.reto}-${w.id}`"
                      >
                        <li>
                          <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadWorkshop(w, block.reto, 'excel')">
                            <i class="fas fa-file-excel text-success me-2" aria-hidden="true"></i>
                            Descargar Excel
                          </a>
                        </li>
                        <li>
                          <a href="javascript:;" class="dropdown-item border-radius-md" @click.stop.prevent="downloadWorkshop(w, block.reto, 'pdf')">
                            <i class="fas fa-file-pdf text-danger me-2" aria-hidden="true"></i>
                            Descargar PDF
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
              <p v-else class="text-sm text-secondary mb-0">Sin asignaciones aún.</p>
            </div>
          </div>
        </div>
      </div>

    </template>

    <template v-else>
      <p class="text-secondary">Cargando datos...</p>
    </template>
  </div>
</template>

<script>
import MiniStatisticsCard from "@/examples/Cards/MiniStatisticsCard.vue";
import axios from "axios";
import moment from "moment";
import es from "moment/dist/locale/es";
moment.updateLocale("es", es);

const PUBLIC_API_URL = process.env.VUE_APP_PUBLIC_API_URL;

const CONTINENT_ORDER = ["América", "Europa", "África"];
const CONTINENT_COLORS = {
  "América": "#d32f2f",
  "Europa": "#1565c0",
  "África": "#ef6c00",
};

export default {
  name: "dashboard-default",
  components: { MiniStatisticsCard },
  data() {
    return {
      isLoading: false,
      selected: "today", // 'today' | 'all' | <week.id>
      currentEntrances: {},
      weeks: [],
      youthByContinent: {},
      infantTeams: [],
      infantTotal: 0,
      retoTotals: [],
      openDownloadMenu: null,
    };
  },
  computed: {
    filterLabel() {
      if (this.selected === "today") return `Hoy (${moment().format("DD MMM YYYY")})`;
      if (this.selected === "all") return "Todo el programa";
      const week = this.weeks.find((w) => w.id === this.selected);
      return week ? `Semana ${week.number} · ${this.formatRange(week)}` : "—";
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      this.isLoading = true;
      await Promise.all([this.fetchWeeksAndTeams(), this.fetchTotals()]);
      this.isLoading = false;
    },
    async fetchWeeksAndTeams() {
      try {
        const [weeks, teams, retoTotals] = await Promise.all([
          axios.get(`${PUBLIC_API_URL}/weeks`),
          axios.get(`${PUBLIC_API_URL}/teams`),
          axios.get(`${PUBLIC_API_URL}/workshops/totals-by-reto`),
        ]);
        this.weeks = weeks.data;
        this.retoTotals = retoTotals.data;

        const grouped = {};
        CONTINENT_ORDER.forEach((c) => (grouped[c] = []));
        this.infantTeams = [];
        this.infantTotal = 0;

        teams.data.forEach((team) => {
          if (team.type === 0 || team.type === "0") {
            this.infantTeams.push(team);
            this.infantTotal += team.members_count || 0;
          } else if (team.active) {
            const continent = team.continent || "Sin continente";
            if (!grouped[continent]) grouped[continent] = [];
            grouped[continent].push(team);
          }
        });

        this.youthByContinent = Object.fromEntries(
          Object.entries(grouped).filter(([, list]) => list.length > 0)
        );
      } catch (error) {
        this.weeks = [];
        this.retoTotals = [];
      }
    },
    async fetchTotals() {
      try {
        const params = {};
        if (this.selected === "all") {
          params.scope = "all";
        } else if (this.selected !== "today") {
          params.week_id = this.selected;
        }
        const response = await axios.get(`${PUBLIC_API_URL}/records/entrances-count`, { params });
        this.currentEntrances = response.data;
      } catch (error) {
        this.currentEntrances = {};
      }
    },
    selectFilter(value) {
      this.selected = value;
      this.fetchTotals();
    },
    continentColor(continent) {
      return CONTINENT_COLORS[continent] || "#344767";
    },
    continentCardStyle(continent) {
      const color = this.continentColor(continent);
      return {
        background: `linear-gradient(180deg, ${this.hexToRgba(color, 0.12)} 0%, ${this.hexToRgba(color, 0.04)} 100%)`,
        borderColor: this.hexToRgba(color, 0.22),
      };
    },
    continentBadgeStyle(continent) {
      return {
        backgroundColor: this.continentColor(continent),
        color: "#fff",
      };
    },
    hexToRgba(hex, alpha) {
      const normalized = hex.replace("#", "");
      const bigint = parseInt(normalized, 16);
      const red = (bigint >> 16) & 255;
      const green = (bigint >> 8) & 255;
      const blue = bigint & 255;
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    continentTotal(paises) {
      return paises.reduce((acc, t) => acc + (t.members_count || 0), 0);
    },
    formatRange(week) {
      return `${moment(week.start_date).format("DD MMM")} – ${moment(week.end_date).format("DD MMM YYYY")}`;
    },
    assigned(block) {
      return (block.workshops || []).filter((w) => w.members_count > 0);
    },
    retoTotal(block) {
      return (block.workshops || []).reduce((acc, w) => acc + (w.members_count || 0), 0);
    },
    openDownload(url) {
      this.openDownloadMenu = null;
      const link = document.createElement("a");
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    toggleDownloadMenu(key) {
      this.openDownloadMenu = this.openDownloadMenu === key ? null : key;
    },
    downloadTeam(team, type, format) {
      const url = `${PUBLIC_API_URL}/team/${team.id}/participants/${type}?team_name=${encodeURIComponent(team.name)}&format=${format}`;
      this.openDownload(url);
    },
    downloadContinent(continent, format) {
      const url = `${PUBLIC_API_URL}/export/youths?report=all&continent=${encodeURIComponent(continent)}&format=${format}`;
      this.openDownload(url);
    },
    downloadWorkshop(workshop, reto, format) {
      const url = `${PUBLIC_API_URL}/workshop/${workshop.id}/participants?reto=${reto}&workshop_name=${encodeURIComponent(workshop.name)}&format=${format}`;
      this.openDownload(url);
    },
  },
};
</script>

<style scoped>
.download-actions {
  display: inline-flex;
}

.download-menu-trigger {
  align-items: center;
  border-radius: 50%;
  display: inline-flex;
  height: 1.75rem;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease;
  width: 1.75rem;
}

.download-menu-trigger:hover,
.download-menu-trigger:focus {
  background-color: rgba(52, 71, 103, 0.1);
  outline: none;
}

.continent-card {
  border: 1px solid;
  box-shadow: 0 0.375rem 1.5rem rgba(20, 20, 20, 0.06);
}

.continent-card :deep(.card-header),
.continent-card :deep(.card-body),
.continent-card :deep(.list-group-item) {
  background: transparent;
}

.continent-total-badge {
  box-shadow: 0 0.25rem 0.75rem rgba(20, 20, 20, 0.12);
}
</style>
